import React, { useState, useRef, useEffect } from 'react';

type SuggestionInputProps = {
  suggestions: string[];
  activeValue?: string;
  loading?: boolean;
  setActiveIndex: (value: number) => void;
  setActiveValue: (value: string) => void;
  maxHeight?: string; // New optional prop for max height
};

const filterSuggestions = (input: string, suggestions: string[]): string[] => {
  if (!input) return suggestions;
  return suggestions.filter((suggestion) =>
    suggestion.toLowerCase().includes(input.toLowerCase())
  );
};

const SuggestionInput: React.FC<SuggestionInputProps> = ({ 
  suggestions,
  setActiveValue,
  loading,
  activeValue,
  setActiveIndex,
  maxHeight = '40vh' // Default max height same as before
}) => {
  const [filteredSuggestions, setFilteredSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLDivElement>(null);

  const handleInputChange = (value: string) => {
    setActiveValue(value)
    const filtered = filterSuggestions(value, suggestions);
    setFilteredSuggestions(filtered);
    setShowSuggestions(!!filtered.length);
  };

  const handleInputFocus = () => {
    setFilteredSuggestions(suggestions);
    setShowSuggestions(true);
  };

  const handleSuggestionClick = (suggestion: string, index: number) => {
    setActiveIndex(index);
    setShowSuggestions(false);
  };

  // Close suggestions on outside click
  const handleClickOutside = (event: MouseEvent) => {
    if (inputRef.current && !inputRef.current.contains(event.target as Node)) {
      setShowSuggestions(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative w-full " ref={inputRef}>
      <input
        disabled={loading}
        type="text"
        value={activeValue}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={handleInputFocus}
        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
        placeholder={loading?"loading...":"Type to search..."}
      />
      {showSuggestions && filteredSuggestions.length === 0 && (
        <p className='absolute z-10 bg-white border p-3 justify-center border-gray-300 w-full rounded-md mt-1 overflow-y-auto shadow-md'>
          No data
        </p>
      )}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <ul 
          className="absolute z-10 bg-white border border-gray-300 w-full rounded-md mt-1 scrollbar overflow-y-auto shadow-md"
          style={{ 
            maxHeight: maxHeight, // Now configurable via prop
            height: 'auto' // Allows dynamic shrinking
          }}
        >
          {filteredSuggestions.map((suggestion, index) => (
            <li
              key={index}
              onClick={() => handleSuggestionClick(suggestion, index)}
              className="p-2 text-gray-700 rounded-md hover:bg-hoverLight hover:text-primary cursor-pointer"
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default SuggestionInput;
